import { useEffect, useRef } from 'react';

export interface IUseTrackOnMountOptions {
  /**
   * Gate firing until the data the event needs is ready. The callback fires on
   * the first render where this is true. Defaults to `true` (fire on mount).
   */
  enabled?: boolean;
}

/**
 * Fires `track` exactly once — on the first render where `enabled` is true.
 *
 * The "fire once when ready" lifecycle is the genuinely error-prone part of
 * landing / page-view analytics: React StrictMode double-invokes effects, the
 * payload data loads asynchronously, and components remount on back-navigation.
 * Centralizing the guard here keeps every call site to one declarative line and
 * keeps the one tricky bit in a single tested place, reusable by any feature.
 *
 * The latest `track` is always used, but changing its identity never re-fires —
 * only the `enabled` transition does.
 */
export function useTrackOnMount(
  track: () => void,
  { enabled = true }: IUseTrackOnMountOptions = {},
): void {
  const hasFiredRef = useRef(false);
  const trackRef = useRef(track);
  trackRef.current = track;

  useEffect(() => {
    if (!enabled || hasFiredRef.current) {
      return;
    }
    hasFiredRef.current = true;
    trackRef.current();
  }, [enabled]);
}
